// Copyright (c) 2021 WiseTime. All rights reserved.

import { EMPTY, Observable, of, throwError } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { getCurrentSiteIdKey } from "../../../app/localStorageKeys"
import { SiteRef } from "generated/wisetime/type/site_ref_pb"
import { sortTeams } from "../../../lib/utils"

export type LoadCurrentSiteIdPayload = {
  userId: string,
  teamSitesList: SiteRef[],
}

export type StorageReader = (payload: LoadCurrentSiteIdPayload) => Observable<{ userId: string, siteId: string }>

export const load: StorageReader = ({ userId, teamSitesList }: LoadCurrentSiteIdPayload) => {
  try {
    const siteId = localStorage.getItem(getCurrentSiteIdKey(userId))
    if (siteId !== null && teamSitesList.some(siteRef => siteRef.id === siteId)) {
      return of({
        userId,
        siteId,
      })
    }
    const [firstSortedTeam] = sortTeams(teamSitesList)
    if (firstSortedTeam) {
      return of({
        userId,
        siteId: firstSortedTeam.id,
      })
    }
    return EMPTY
  } catch (error) {
    return throwError(error)
  }
}

export const loadCurrentSiteId = (load: StorageReader) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Slice.setUserDetails.match),
      mergeMap(({ payload: { user }}) =>
        of({
          userId: user.id,
          teamSitesList: user.teamSites,
        }).pipe(
          mergeMap(load),
          map(Slice.setCurrentSiteId),
          catchError(_ => of(Actions.loadCurrentSiteIdFailed())),
        )),
    )

export default loadCurrentSiteId(load)
